import React from 'react';
import { Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconCreditCard, IconBuildingBank, IconShieldLock, IconArrowBackUp, IconLock, IconClockHour4 } from '@tabler/icons-react';
import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { MoneyLocation, currentMoneyLocation } from '../helpers/bookingTimelineHelper';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import { SURFACE_1_SOFT, TEAL, TEAL_SOFT_BG } from '../theme/creamyTokens';

const { Title, Text } = Typography;

const MONEY_ICON: Record<MoneyLocation, React.ElementType> = {
  guest: IconCreditCard,
  held: IconLock,
  platform: IconShieldLock,
  host: IconBuildingBank,
  refunded: IconArrowBackUp,
};

const MONEY_LABEL_KEY: Record<MoneyLocation, string> = {
  guest: TRANSLATIONS.BOOKING_TIMELINE_MONEY_GUEST,
  held: TRANSLATIONS.BOOKING_TIMELINE_MONEY_HELD,
  platform: TRANSLATIONS.BOOKING_TIMELINE_MONEY_PLATFORM,
  host: TRANSLATIONS.BOOKING_TIMELINE_MONEY_HOST,
  refunded: TRANSLATIONS.BOOKING_TIMELINE_MONEY_REFUNDED,
};

const MONEY_DETAIL_KEY: Record<MoneyLocation, string> = {
  guest: TRANSLATIONS.BOOKING_TIMELINE_MONEY_GUEST_DETAIL,
  held: TRANSLATIONS.BOOKING_TIMELINE_MONEY_HELD_DETAIL,
  platform: TRANSLATIONS.BOOKING_TIMELINE_MONEY_PLATFORM_DETAIL,
  host: TRANSLATIONS.BOOKING_TIMELINE_MONEY_HOST_DETAIL,
  refunded: TRANSLATIONS.BOOKING_TIMELINE_MONEY_REFUNDED_DETAIL,
};

// What became of the hold, in the guest's own terms. Keyed by the
// backend's DepositStatus strings rather than a narrowed union, since the
// field is a plain string on the wire and an unrecognised value should fall
// back to the neutral wording rather than render blank.
const DEPOSIT_DETAIL_KEY: Record<string, string> = {
  None: TRANSLATIONS.BOOKING_TIMELINE_DEPOSIT_DETAIL,
  Authorized: TRANSLATIONS.BOOKING_TIMELINE_DEPOSIT_DETAIL,
  Released: TRANSLATIONS.BOOKING_TIMELINE_DEPOSIT_RELEASED,
  PartiallyCaptured: TRANSLATIONS.BOOKING_TIMELINE_DEPOSIT_CAPTURED,
  FullyCaptured: TRANSLATIONS.BOOKING_TIMELINE_DEPOSIT_CAPTURED,
};

interface BookingMoneyCardProps {
  booking: IBooking;
}

/**
 * "¿Dónde está el dinero?" -- the plain-language answer to the question both
 * sides of a rental actually have, next to the timeline that explains why.
 *
 * Two amounts, deliberately kept apart: the RENTAL (charged, and sitting in
 * whichever balance currentMoneyLocation reports) and the DEPOSIT (only ever
 * held on the guest's own card, never charged unless there's damage). Users
 * routinely read a deposit hold as a second charge, so showing them as one
 * number -- or in the same visual block -- is exactly the confusion worth
 * spending a card to avoid.
 */
const BookingMoneyCard: React.FC<BookingMoneyCardProps> = ({ booking }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const location = currentMoneyLocation(booking);

  // While the hold is still live, the most useful thing to say about it is
  // not "it's a hold" but HOW MUCH of it is actually at risk. With cover in
  // place that ceiling is the franquicia, not the whole deposit -- and it is
  // usually a much smaller, much less alarming number. Once the hold has
  // been resolved the outcome matters more than the ceiling, so the
  // settled wording wins.
  const stillHeld = (booking.depositStatus ?? 'None') === 'Authorized' || (booking.depositStatus ?? 'None') === 'None';
  // The hold lapsed but the fianza has not been resolved -- so something is
  // still owed in principle and nothing is frozen in practice. This is the
  // case the card used to lie about outright, and it is the common one on
  // any rental longer than a few days: a card authorisation lives about a
  // week, and the hold goes on 48 hours before the machine even leaves.
  const coverageIsCardOnly = booking.depositCoverage === 'SavedCard';
  const cappedByExcess = stillHeld && !coverageIsCardOnly && !!booking.insuranceExcessAmount && booking.insuranceExcessAmount > 0;
  const depositDetailKey = coverageIsCardOnly
    ? TRANSLATIONS.BOOKING_TIMELINE_DEPOSIT_COVERAGE_CARD
    : cappedByExcess
      ? TRANSLATIONS.BOOKING_TIMELINE_DEPOSIT_CAPPED
      : DEPOSIT_DETAIL_KEY[booking.depositStatus ?? 'None'] ?? TRANSLATIONS.BOOKING_TIMELINE_DEPOSIT_DETAIL;
  const depositDetailParams = cappedByExcess
    ? { excess: formatCurrency(booking.insuranceExcessAmount!, booking.hostCurrencyCode, i18n.language) }
    : undefined;
  // Said out loud while the hold IS alive, because it is the one fact that
  // stops the later expiry reading as something going wrong.
  const holdUntil =
    booking.depositCoverage === 'Hold' && booking.depositHoldExpiresAt
      ? t(TRANSLATIONS.BOOKING_TIMELINE_DEPOSIT_HOLD_UNTIL, {
          date: new Date(booking.depositHoldExpiresAt).toLocaleDateString(i18n.language),
        })
      : undefined;
  const Icon = MONEY_ICON[location];
  // Only the settled outcomes read teal; money still in flight stays neutral
  // so "done" and "in progress" never look alike.
  const settled = location === 'host' || location === 'refunded';

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 16,
        padding: 16,
      }}
    >
      <Title level={5} style={{ margin: '0 0 12px' }}>
        {translate(t, TRANSLATIONS.BOOKING_TIMELINE_MONEY_TITLE)}
      </Title>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span
          style={{
            display: 'flex',
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            background: settled ? TEAL_SOFT_BG : SURFACE_1_SOFT,
            color: settled ? TEAL : token.colorTextSecondary,
          }}
        >
          <Icon size={20} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
            <Text strong style={{ fontSize: 15 }}>
              {translate(t, MONEY_LABEL_KEY[location])}
            </Text>
            <Text strong style={{ fontSize: 15, whiteSpace: 'nowrap' }}>
              {formatCurrency(booking.guestChargeAmount, booking.guestCurrencyCode, i18n.language)}
            </Text>
          </div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {translate(t, MONEY_DETAIL_KEY[location])}
          </Text>
        </div>
      </div>

      {/* Anything the rental cost beyond the price agreed up front. Shown
          on its own line rather than folded into the total above, because
          an extra charge that appears inside a number the guest already
          recognised is exactly how a card charge becomes a dispute. */}
      {!!booking.overtimeAmountBilled && booking.overtimeAmountBilled > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            marginTop: 14,
            paddingTop: 14,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <span style={{ display: 'flex', flexShrink: 0, color: token.colorTextTertiary, marginTop: 2 }}>
            <IconClockHour4 size={16} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <Text style={{ fontSize: 14 }}>{translate(t, TRANSLATIONS.BOOKING_TIMELINE_OVERTIME_LABEL)}</Text>
              <Text style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                {formatCurrency(booking.overtimeAmountBilled, booking.hostCurrencyCode, i18n.language)}
              </Text>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translate(t, TRANSLATIONS.BOOKING_TIMELINE_OVERTIME_DETAIL, {
                hours: booking.overtimeHoursBilled?.toFixed(1) ?? '0',
              })}
            </Text>
          </div>
        </div>
      )}

      {/* A deposit is a hold on the guest's own card, never part of the
          amount above -- see this component's own doc comment. */}
      {!!booking.depositAmount && booking.depositAmount > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            marginTop: 14,
            paddingTop: 14,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <span style={{ display: 'flex', flexShrink: 0, color: token.colorTextTertiary, marginTop: 2 }}>
            <IconLock size={16} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <Text style={{ fontSize: 14 }}>{translate(t, TRANSLATIONS.BOOKING_TIMELINE_DEPOSIT_LABEL)}</Text>
              <Text style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                {formatCurrency(booking.depositAmount, booking.hostCurrencyCode, i18n.language)}
              </Text>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translate(t, depositDetailKey, depositDetailParams)}
            </Text>
            {holdUntil && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {holdUntil}
              </Text>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingMoneyCard;
